import {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {OmniContext} from "@/OmniContext.ts";
import {
    Alert,
    Badge,
    Button,
    Center,
    ComboboxParsedItemGroup,
    Popover,
    Select,
    Stack,
    Text,
    Textarea,
    Tooltip,
    useComputedColorScheme
} from "@mantine/core";
import {IActivePunch, IEntryTask} from "@/types/models/ITimecard.ts";
import {getHotkeyHandler, useDebouncedState, useInterval, useLocalStorage} from "@mantine/hooks";
import OmniFetchTS from "@/util/OmniFetchTS.tsx";
import {IAddInPunch, IAddOutPunch, IGetActivePunch, IGetPunchTasks} from "@/types/api/Timecard.ts";
import TooltipActionIcon, {TooltipActionIconProps} from "@/components/TooltipActionIcon.tsx";
import {IconClock, IconClockPlay, IconClockStop, IconToolsKitchen3} from "@tabler/icons-react";
import {IProject} from "@/types/models/IProject.ts";
import {notifications} from "@mantine/notifications";
import {DateTime, Duration} from "luxon";
import {SysPerm} from "@/util/SysPerm.ts";
import {useBrowserNotifications} from "@/util/use-browser-notifications.ts";
import {TIME_CLOCK_NOTIFICATIONS_FREQUENCY_LOCAL_STORAGE_PROPS} from "@/util/constants.ts";
import {
    TimeClockNotificationsFrequency,
    timeClockNotificationsFrequencyToMinutes
} from "@/types/enums/TimeClockNotificationsFrequency.ts";

const CURRENT_PUNCH_REFRESH_INTERVAL_MS = 30000
const LUNCH_CLOCK_UPDATE_INTERVAL_MS = 500
const LUNCH_REQUIRED_MINUTES = 30

function getProjectIdentifier(project: IProject) {
    return `${project.customer?.name || "Customer"} - ${project.customer?.customer_number || "XXX"}-${project.project_number} - ${project.name}`
}

function generateElapsedClockDisplay(diff: Duration) {
    if (diff != null) {
        if (diff.hours >= 1) {
            return `${diff.hours}:${Math.floor(diff.minutes).toString().padStart(2, "0")}:${Math.floor(diff.seconds).toString().padStart(2, "0")}`
        } else if (diff.minutes > 0 || diff.seconds > 0) {
            return `${Math.floor(diff.minutes)}:${Math.floor(diff.seconds).toString().padStart(2, "0")}`
        } else {
            return "0:00"
        }
    }
}

function generateElapsedDisplay(diff: Duration) {
    if (diff != null) {
        let hoursFloor = Math.floor(diff.hours)
        let minutesFloor = Math.floor(diff.minutes)
        return `${hoursFloor > 0 ? `${hoursFloor} hour${hoursFloor > 1 ? "s" : ""}` : ""}${minutesFloor > 0 ? `${hoursFloor > 0 ? " and " : ""}${minutesFloor} minutes` : ""}`
    }
}

export default function TimeClock() {
    const context = useContext(OmniContext)

    const colorScheme = useComputedColorScheme();

    const [punch, setPunch] = useState<IActivePunch | null>(null)

    const [projects, setProjects] = useState<IProject[]>([])

    const [tasks, setTasks] = useState<IEntryTask[]>([])

    /**
     * The comments field for the active task
     */
    const [comments, setComments] = useState<string>("")

    /**
     * The debounced comments field. When this changes, update the backend.
     */
    const [debouncedComments, setDebouncedComments] = useDebouncedState("", 200);

    /**
     * The errors, if applicable, for the current value of the comments field
     */
    const [commentsErrors, setCommentsErrors] = useState<string>("")

    /**
     * How much longer the user has to wait to end their lunch
     */
    const [elapsedClockDisplay, setElapsedClockDisplay] = useState<string>("")

    const [timeClockNotificationsFrequency] = useLocalStorage(TIME_CLOCK_NOTIFICATIONS_FREQUENCY_LOCAL_STORAGE_PROPS)

    const {sendNotification} = useBrowserNotifications()

    /**
     * Format the lunch clock
     */
    const updateElapsedClockDisplay = useCallback((activePunch?: IActivePunch | null, sendNotifications: boolean = true) => {
        if (activePunch == undefined) activePunch = punch
        let elapsedDiff: Duration | null = null
        let diff: Duration | null = null
        if (activePunch) {
            let punchIn = DateTime.fromISO(activePunch.start_time)
            elapsedDiff = DateTime.now().diff(punchIn, ["hours", "minutes", "seconds", "milliseconds"])
            if (activePunch.task_id == null) {
                let lunchEnd = DateTime.fromISO(activePunch.start_time).plus({minute: LUNCH_REQUIRED_MINUTES})
                diff = lunchEnd.diff(DateTime.now(), ["hours", "minutes", "seconds", "milliseconds"])
            } else {
                diff = elapsedDiff
            }
        } else if (elapsedClockDisplay) {
            setElapsedClockDisplay("")
            return
        }
        if (diff != null && elapsedDiff != null) {
            let hoursFloor = Math.floor(elapsedDiff.hours)
            let minutesFloor = Math.floor(elapsedDiff.minutes)
            if (
                timeClockNotificationsFrequency != TimeClockNotificationsFrequency.NEVER
                && (hoursFloor == 0 ? minutesFloor != 0 : true)
                && minutesFloor % timeClockNotificationsFrequencyToMinutes(timeClockNotificationsFrequency as TimeClockNotificationsFrequency) == 0
                && Math.floor(elapsedDiff.seconds) == 0 && elapsedDiff.milliseconds < 500
                && sendNotifications
            ) {
                if (punch && punch.task) {
                    let message = `You have been on ${projects.map((p) => p.id == punch.task?.project_id ? `${p.customer?.customer_number}-${p.project_number}` : "")}: ${punch.task?.name} for ${generateElapsedDisplay(elapsedDiff)}`
                    sendNotification("Time Clock Reminder", {body: message, tag: `${punch.start_time}${message}`, renotify: false}).then()
                } else if (punch) {
                    let message = `You have been on lunch for ${generateElapsedDisplay(elapsedDiff.plus({seconds: 1}).shiftTo("hours", "minutes"))}`
                    sendNotification("Time Clock Reminder", {body: message, tag: `${punch.start_time}${message}`, renotify: false}).then()
                }
            }
            setElapsedClockDisplay(generateElapsedClockDisplay(diff) ?? "")
        }
    }, [elapsedClockDisplay, punch, projects, sendNotification, timeClockNotificationsFrequency])

    /**
     * Update the lunch clock every half second
     */
    const elapsedClockUpdateInterval = useInterval(() => {
        updateElapsedClockDisplay()
    }, LUNCH_CLOCK_UPDATE_INTERVAL_MS)

    useEffect(() => {
        elapsedClockUpdateInterval.start()
        return elapsedClockUpdateInterval.stop
    }, [elapsedClockUpdateInterval]);

    /**
     * Get the active punch for the user
     */
    const getActivePunch = useCallback(() => {
        if (context.user) {
            OmniFetchTS<IGetActivePunch>(`/api/v1/timecard/active_punch?user_id=${context.user.id}`).then((json) => {
                if (json.success && json.data) {
                    updateElapsedClockDisplay(json.data.punch)
                    setPunch(json.data.punch);
                    setComments(json.data.punch?.comments ?? "");
                }
            })
        }
    }, [context.user, updateElapsedClockDisplay])

    /**
     * Get the tasks and projects available to the user today
     */
    const getAvailableTasks = useCallback(() => {
        if (context.user) {
            OmniFetchTS<IGetPunchTasks>(`/api/v1/timecard/punch_tasks?user_id=${context.user.id}`).then((json) => {
                if (json.success && json.data) {
                    setProjects(json.data.projects)
                    setTasks(json.data.tasks)
                }
            })
        }
    }, [context.user])

    /**
     * Repeatedly check for punch updates
     */
    const refreshInterval = useInterval(() => {
        getActivePunch()
    }, CURRENT_PUNCH_REFRESH_INTERVAL_MS, {autoInvoke: true})

    /**
     * Whether the user has bypassed the lunch clock
     */
    const [bypassLunchTimer, setBypassLunchTimer] = useState<boolean>(false)

    const [retrievedFirstPunch, setRetrievedFirstPunch] = useState<boolean>(false)

    /**
     * Get the current punch when the component mounts
     */
    useEffect(() => {
        if (!retrievedFirstPunch && context.user) {
            getActivePunch()
            setRetrievedFirstPunch(true)
        }
    }, [context.user, getActivePunch, retrievedFirstPunch]);

    /**
     * Start the current punch checker when the component mounts, stop it when the component unmounts
     */
    useEffect(() => {
        refreshInterval.start()
        return refreshInterval.stop
    }, [getActivePunch, refreshInterval])

    /**
     * When the time clock is expanded, update the current punch and available tasks
     */
    const onOpen = useCallback(() => {
        getActivePunch()
        getAvailableTasks()
    }, [getActivePunch, getAvailableTasks])

    /**
     * The list of tasks that the user can select from, grouped by project
     */
    const comboboxTasks = useMemo<ComboboxParsedItemGroup[]>(() => {
        let comboboxTasks: ComboboxParsedItemGroup[] = []
        if (punch && punch.task && punch.project) {
            comboboxTasks.push({group: getProjectIdentifier(punch.project), items: [{value: `${punch.task.id}`, label: punch.task.name}]})
        }
        for (let task of tasks) {
            if (task.id == punch?.task_id) continue
            for (let project of projects) {
                if (task.project_id == project.id) {
                    let projectIdentifier = getProjectIdentifier(project)
                    let foundGroupIndex: number | null = null
                    for (let i = 0; i < comboboxTasks.length; i++) {
                        if (comboboxTasks[i].group == projectIdentifier) {
                            foundGroupIndex = i
                            break
                        }
                    }
                    if (foundGroupIndex != null) {
                        comboboxTasks[foundGroupIndex].items.push({value: `${task.id}`, label: task.name})
                    } else {
                        comboboxTasks.push({group: projectIdentifier, items: [{value: `${task.id}`, label: task.name}]})
                    }

                    break
                }
            }
        }
        return comboboxTasks
    }, [projects, punch, tasks])

    /**
     * Hide the select (in favor of buttons only for check in/switch task) if the user has one or fewer tasks
     */
    const hideSelect = useMemo(() => tasks.length <= 1, [tasks])

    /**
     * Start debouncing comments input
     */
    useEffect(() => {
        setDebouncedComments(comments);
    }, [comments, setDebouncedComments]);

    /**
     * Save comments to the backend
     */
    useEffect(() => {
        if (!context.user || !punch) {
            return;
        }
        const body = {
            user_id: context.user.id,
            comments: debouncedComments,
        };
        OmniFetchTS("/api/v1/timecard/punch/comments", {method: "PUT"}, body)
    }, [debouncedComments, context.user, punch]);
    
    /**
     * Clock the user in
     * @param taskId the ID of the task to clock in to, or null to start a lunch
     */
    const clockIn = useCallback((taskId: number | null) => {
        if (context.user) {
            OmniFetchTS<IAddInPunch>("/api/v1/timecard/punch", {method: "POST"}, {user_id: context.user.id, task_id: taskId}).then((json) => {
                if (json.success && json.data) {
                    if (json.data.punch.project && json.data.punch.task) {
                        notifications.show({title: punch?.task_id ? "Switched Task" : "Clocked In", color: "green", message: `Working on ${json.data.punch.project.customer?.name} - ${json.data.punch.project.name} - ${json.data.punch.task.name}`})
                    } else {
                        setBypassLunchTimer(false)
                        updateElapsedClockDisplay(json.data.punch)
                        notifications.show({title: "Started Lunch", color: "green", message: "Enjoy your meal!"})
                    }
                    updateElapsedClockDisplay(json.data.punch)
                    setPunch(json.data.punch)
                }
            })
        }
    }, [context.user, punch?.task_id, updateElapsedClockDisplay])

    /**
     * Clock the user out
     * @param updatePunch whether to call setPunch when the response is received
     * @param validateComments whether to validate the comments field
     * @return Promise<boolean> whether the out punch succeeded
     */
    const clockOut = useCallback((updatePunch: boolean = true, validateComments: boolean = false) => {
        return new Promise<boolean>((resolve) => {
            if (validateComments && punch?.task?.comments_required && comments.trim() == "") {
                setCommentsErrors("Comments are required for the current task. Please enter comments before switching tasks.")
                resolve(false)
                return
            }
            if (context.user) {
                const body = {
                    user_id: context.user.id,
                    comments: comments,
                }
                OmniFetchTS<IAddOutPunch>(`/api/v1/timecard/punch`, {method: "DELETE"}, body).then((json) => {
                    if (json.success) {
                        if (updatePunch) {
                            setPunch(null)
                            notifications.show({title: "Clocked Out", color: "green", message: `Have a nice day!`})
                        }
                        setComments("")
                        resolve(true)
                    } else {
                        resolve(false)
                    }
                })
            } else {
                resolve(false)
            }
        })
    }, [comments, context.user, punch?.task?.comments_required])

    /**
     * Change the current task (clock out, then clock in)
     * @param taskId the task to start, or null to start a lunch
     */
    const changeTask = useCallback((taskId: number | null) => {
        clockOut(false, true).then((success) => {
            if (success && context.user) {
                clockIn(taskId)
            }
        })
    }, [clockIn, clockOut, context.user])

    /**
     * The ID of the currently selected task, otherwise null
     */
    const selectedTask = useMemo<string | null>(() => (punch && punch.task_id) ? `${punch.task_id}` : null, [punch])

    /**
     * Start or switch to the selected task, validating that comments are entered if they're required
     * @param taskId the task to change to
     */
    const changeSelectedTask = useCallback((taskId: string | null) => {
        let selectedTaskId = taskId ? parseInt(taskId) : -1
        let taskIdInt = hideSelect ? tasks[0].id : selectedTaskId
        if (punch) {
            if (punch.task?.comments_required && comments.trim() == "") {
                setCommentsErrors("Comments are required for the current task. Please enter comments before switching tasks.")
            } else {
                changeTask(taskIdInt)
            }
        } else {
            clockIn(taskIdInt)
        }
    }, [changeTask, clockIn, comments, hideSelect, punch, tasks])

    /**
     * Clock in to the default task, used only if the user has one available task
     */
    const clockInDefault = useCallback(() => {
        clockIn(tasks[0].id)
    }, [clockIn, tasks])

    /**
     * End lunch and clock in to the default task, used only if the user has one available task
     */
    const endLunchAndClockIn = useCallback(() => {
        clockOut(false).then((success) => {
            if (success && context.user) {
                clockInDefault()
            }
        })
    }, [clockInDefault, clockOut, context.user])

    /**
     * Generate the look of the Time Clock icon in the navbar
     */
    const navbarIconProps = useMemo<TooltipActionIconProps>(() => {
        let navbarIconProps: TooltipActionIconProps = {label: "Time Clock", size: "lg", actionIconProps: {variant: "subtle"}, tooltipProps: {position: "left"}, children: <IconClock/>}
        if (punch == null) {
            navbarIconProps.c = "red"
            navbarIconProps.children = <IconClockStop/>
        } else if (punch.task_id == null) {
            navbarIconProps.c = "violet"
            navbarIconProps.children = <IconToolsKitchen3/>
        } else {
            navbarIconProps.c = "green"
            navbarIconProps.children = <IconClockPlay/>
        }
        return navbarIconProps
    }, [punch])

    if (
        (
            !context.user // if we don't have the user context
            || !context.user.hasSystemPermission(SysPerm.TIMECARD_USER) // or the user isn't a timecard user
            || (context.user.hasSystemPermission(SysPerm.TIMECARD_TIMELESS_EDIT_ENABLED) && !context.user.pref_time_clock_enabled) // or the user is salaried and doesn't want the time clock
        ) // then we don't show the time clock
        && punch == null // unless they have an active punch
    ) {
        return null
    }

    return <Popover position={"bottom"} width={300} trapFocus onOpen={onOpen}>
        <Popover.Target>
            <TooltipActionIcon {...navbarIconProps}/>
        </Popover.Target>
        <Popover.Dropdown>
            {punch == null && <Stack>
                <Badge fullWidth size={"xl"} color={"red"}>Clocked Out</Badge>
                {tasks.length == 0
                    ? <Alert color={"red"}><Center>No Tasks Assigned</Center></Alert>
                    : hideSelect
                        ? <Button disabled={!(hideSelect || selectedTask != null)} fullWidth onClick={clockInDefault} color={"green"}>Clock In</Button>
                        : <Select required label={"Task"} data={comboboxTasks} comboboxProps={{withinPortal: false}} value={null} onChange={changeSelectedTask} searchable placeholder={"Select a Task to Clock In"}/>
                }
            </Stack>}
            {(punch && punch.task == null) && <Stack>
                <Badge fullWidth size={"xl"} color={"violet"}>On Lunch</Badge>
                {elapsedClockDisplay != "0:00" && <Alert color={"violet"} title={"Required Lunch Time Remaining"} styles={{title: {justifyContent: "center"}}}>
                    <Stack align={"center"}>
                        {!bypassLunchTimer && <Button.Group>
                            <Button.GroupSection variant={"outline"} color={"violet"} size={"compact-md"} c={colorScheme == "dark" ? "white" : "black"}>
                                <Tooltip label={DateTime.fromISO(punch.start_time).plus({minute: LUNCH_REQUIRED_MINUTES}).toLocaleString(DateTime.TIME_WITH_SECONDS)}>
                                    <Text fw="bold">
                                        {elapsedClockDisplay}
                                    </Text>
                                </Tooltip>
                            </Button.GroupSection>
                            <Button color={"violet"} size={"compact-md"} onClick={() => {setBypassLunchTimer(true)}}>BYPASS</Button>
                        </Button.Group>}
                        {bypassLunchTimer && <Text fz={"lg"}>Bypassed Lunch Timer</Text>}
                    </Stack>
                </Alert>}
                {(elapsedClockDisplay == "0:00" || bypassLunchTimer) && <>
                    {tasks.length == 0
                        ? <Alert color={"red"}><Center>No Tasks Assigned</Center></Alert>
                        : hideSelect
                            ? <Button disabled={!(hideSelect || selectedTask != null)} fullWidth onClick={endLunchAndClockIn} color={"green"}>Clock In</Button>
                            : <Select required label={"Task"} data={comboboxTasks} comboboxProps={{withinPortal: false}} value={null} onChange={changeSelectedTask} searchable placeholder={"Select a Task to Clock In"}/>
                    }
                    <Button fullWidth onClick={() => clockOut()} color={"red"}>Clock Out</Button>
                </>}
            </Stack>}
            {(punch && punch.task && punch.project) && <Stack>
                <Badge fullWidth size={"xl"} color={"green"}>
                    <Text fw="bold">
                        Clocked In -
                        <Tooltip label={DateTime.fromISO(punch.start_time).toLocaleString(DateTime.TIME_WITH_SECONDS)}>
                            <Text span fw="bold" pl="4px">
                                {elapsedClockDisplay}
                            </Text>
                        </Tooltip>
                    </Text>
                </Badge>
                {!hideSelect && <Select required allowDeselect={false} label={"Task (change me to switch tasks)"} data={comboboxTasks} comboboxProps={{withinPortal: false}} value={selectedTask} onChange={changeSelectedTask} searchable/>}
                <Textarea
                    label={
                        `Comments ${punch.task.comments_required ? "" : "(optional)" }`
                    }
                    required={punch.task.comments_required}
                    minRows={1}
                    maxRows={4}
                    autosize
                    value={comments}
                    onChange={(e) => {
                        setComments(e.target.value)
                        setCommentsErrors("")
                    }}
                    error={commentsErrors}
                    onKeyDown={getHotkeyHandler([
                        ["mod + Enter", () => clockOut(true, true)]
                    ])}
                />
                <Button fullWidth onClick={() => clockOut(true, true)} color={"red"}>Clock Out</Button>
                <Button fullWidth onClick={() => changeTask(null)} color={"violet"}>Start Lunch</Button>
            </Stack>}
        </Popover.Dropdown>
    </Popover>
}